<template>
    <div class="overflow-clip rounded-md border border-gray-200 bg-white">

        <!-- Row header -->
        <button
            type="button"
            class="flex w-full items-center gap-4 px-6 py-4 text-left transition-colors duration-150 hover:bg-gray-50"
            @click="expanded = !expanded"
        >
            <!-- Icon -->
            <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-500 text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
            </div>

            <!-- Title + description -->
            <div class="flex-1">
                <p class="m-0 text-sm font-semibold text-gray-900">Export Users</p>
                <p class="m-0 text-sm text-gray-500">Export users and their highest permission level for a company and its children.</p>
            </div>

            <!-- Chevron -->
            <svg class="h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200"
                 :class="{ 'rotate-90': expanded }"
                 xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
        </button>

        <!-- Expanded filter panel -->
        <div v-if="expanded" class="border-t border-gray-200 bg-gray-50 px-6 py-5">

            <!-- Banners -->
            <div class="mb-4">

                <div v-if="showSuccess" class="flex items-start gap-3 rounded-md border border-green-500/20 bg-green-50 px-4 py-3 border-l-4 border-l-green-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-green-800">Export Started</p>
                        <p class="m-0 text-sm text-green-700">You will receive an email with the CSV shortly.</p>
                    </div>
                    <button type="button" class="text-green-500/70 hover:text-green-700/70" @click="showSuccess = false">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

                <div v-if="showError" class="flex items-start gap-3 rounded-md border border-red-500/20 bg-red-50 px-4 py-3 border-l-4 border-l-red-500/70">
                    <div class="flex-1">
                        <p class="m-0 text-sm font-semibold text-red-800">Export Failed</p>
                        <p class="m-0 text-sm text-red-700">{{ errorMessage }}</p>
                    </div>
                    <button type="button" class="text-red-500/70 hover:text-red-700/70" @click="showError = false">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>

            </div>

            <div class="grid grid-cols-1 gap-4">

                <!-- Company select -->
                <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700" for="export-users-company">
                        Company <span class="text-red-500">*</span>
                    </label>
                    <select2
                        id="export-users-company"
                        v-model:value="companyId"
                        :options="companies"
                        name="company_id"
                    ></select2>
                </div>

            </div>

            <!-- Footer row -->
            <div class="mt-4 flex items-center justify-end">

                <button
                    type="button"
                    :disabled="exporting"
                    @click="handleExport"
                    class="inline-flex min-w-80 items-center justify-center rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-600 focus:outline-none active:bg-blue-700 disabled:opacity-50"
                >
                    <svg v-if="exporting" class="mr-2 h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg"
                         fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    {{ exporting ? 'Exporting...' : 'Export Users' }}
                </button>
            </div>
        </div>

    </div>
</template>

<script>
import axios from "axios";
import Select2 from "@/components/elements/select2.vue";

export default {
    name: "ExportUsersPanel",
    components: {Select2},
    props: {
        companies: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            expanded: false,
            companyId: null,
            exporting: false,
            showSuccess: false,
            showError: false,
            errorMessage: 'Something went wrong. Please try again.',
            _successTimer: null,
        }
    },
    beforeUnmount() {
        clearTimeout(this._successTimer);
    },
    methods: {
        handleExport() {
            if (this.exporting) {
                return;
            }

            if (!this.companyId) {
                this.showError = true;
                this.errorMessage = 'Please select a company before exporting.';
                return;
            }

            this.exporting = true;
            this.showSuccess = false;
            this.showError = false;

            axios.post('/api-internal/export-users', {
                company_id: this.companyId,
            }).then(() => {
                this.showSuccess = true;
                clearTimeout(this._successTimer);
                this._successTimer = setTimeout(() => { this.showSuccess = false; }, 5000);
            }).catch(() => {
                this.errorMessage = 'Something went wrong. Please try again.';
                this.showError = true;
            }).finally(() => {
                this.exporting = false;
            });
        },
    },
}
</script>

